/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import Field from '@atlaskit/form/Field';
import PriorityCriticalIcon from '@atlaskit/icon/core/priority-critical';
import PriorityHighIcon from '@atlaskit/icon/core/priority-high';
import PriorityHighestIcon from '@atlaskit/icon/core/priority-highest';
import PriorityLowIcon from '@atlaskit/icon/core/priority-low';
import PriorityMediumIcon from '@atlaskit/icon/core/priority-medium';
import { Box, Flex, Inline, Text } from '@atlaskit/primitives/compiled';
import Select from '@atlaskit/select/select';
import type { FormatOptionLabelMeta, StylesConfig } from '@atlaskit/select/types';
import { token } from '@atlaskit/tokens';

import messages from '../../messages';
import { type SpfAskPriority } from '../../types';

const priorityIcons: Record<SpfAskPriority, ReactNode> = {
	LOW: <PriorityLowIcon label="" color={token('color.icon.information')} />,
	MEDIUM: <PriorityMediumIcon label="" color={token('color.icon.warning')} />,
	HIGH: <PriorityHighIcon label="" color={token('color.icon.danger')} />,
	HIGHEST: <PriorityHighestIcon label="" color={token('color.icon.danger')} />,
	CRITICAL: <PriorityCriticalIcon label="" color={token('color.icon.danger')} />,
};

type PriorityOption = {
	label: string;
	value: SpfAskPriority;
};

const COMPACT_CONTROL_HEIGHT = '2.5rem';

const styles = cssMap({
	compactSubtleControl: {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		height: COMPACT_CONTROL_HEIGHT,
		minWidth: '0',
		borderRadius: token('radius.small'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
});

const compactSubtleSelectStyles: StylesConfig<PriorityOption, false> = {
	container: (base) => ({
		...base,
		width: '100%',
		height: COMPACT_CONTROL_HEIGHT,
		minWidth: 0,
	}),
	control: (base) => ({
		...base,
		alignItems: 'center',
		backgroundColor: 'transparent',
		border: 'none',
		boxShadow: 'none',
		cursor: 'pointer',
		height: COMPACT_CONTROL_HEIGHT,
		minHeight: COMPACT_CONTROL_HEIGHT,
		width: '100%',
		'&:hover': {
			backgroundColor: 'transparent',
			border: 'none',
			boxShadow: 'none',
		},
	}),
	valueContainer: (base) => ({
		...base,
		alignItems: 'center',
		display: 'flex',
		height: COMPACT_CONTROL_HEIGHT,
		minWidth: 0,
		paddingTop: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.050'),
	}),
	input: (base) => ({
		...base,
		marginTop: token('space.0'),
		marginBottom: token('space.0'),
		paddingTop: token('space.0'),
		paddingBottom: token('space.0'),
	}),
	singleValue: (base) => ({
		...base,
		marginLeft: token('space.0'),
		marginRight: token('space.0'),
		maxWidth: '100%',
	}),
	indicatorsContainer: (base) => ({
		...base,
		alignItems: 'center',
		height: COMPACT_CONTROL_HEIGHT,
	}),
	dropdownIndicator: (base) => ({
		...base,
		paddingTop: token('space.050'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.075'),
	}),
};

type Props = {
	value: SpfAskPriority;
	onChange: (value: SpfAskPriority) => void;
	hideLabel?: boolean;
	isCompact?: boolean;
	width?: number | string;
	appearance?: 'default' | 'subtle';
	isDisabled?: boolean;
};

export const PriorityField = ({
	value,
	onChange,
	hideLabel = false,
	isCompact = false,
	width = '100%',
	appearance,
	isDisabled = false,
}: Props): JSX.Element => {
	const { formatMessage } = useIntl();
	const labelText = formatMessage(messages.priorityFieldLabel);

	const priorityOptions: PriorityOption[] = useMemo(
		() => [
			{ label: formatMessage(messages.priorityLow), value: 'LOW' },
			{ label: formatMessage(messages.priorityMedium), value: 'MEDIUM' },
			{ label: formatMessage(messages.priorityHigh), value: 'HIGH' },
			{ label: formatMessage(messages.priorityHighest), value: 'HIGHEST' },
			{ label: formatMessage(messages.priorityCritical), value: 'CRITICAL' },
		],
		[formatMessage],
	);

	const selectedPriority = useMemo(
		() => priorityOptions.find((o) => o.value === value) ?? priorityOptions[1],
		[value, priorityOptions],
	);

	const formatPriorityOptionLabel = (
		option: PriorityOption,
		_meta: FormatOptionLabelMeta<PriorityOption>,
	) => (
		<Inline space="space.100" alignBlock="center">
			<Flex role="none" justifyContent="center" alignItems="center">
				{priorityIcons[option.value]}
			</Flex>
			<Text>{option.label}</Text>
		</Inline>
	);

	const select = (
		<Select<PriorityOption>
			options={priorityOptions}
			value={selectedPriority}
			onChange={(selected) => onChange(selected?.value ?? 'MEDIUM')}
			formatOptionLabel={formatPriorityOptionLabel}
			testId="focus-ask-priority-field"
			spacing={isCompact ? 'compact' : 'default'}
			width={width}
			appearance={appearance}
			styles={isCompact && appearance === 'subtle' ? compactSubtleSelectStyles : undefined}
			aria-label={hideLabel ? labelText : undefined}
			isDisabled={isDisabled}
		/>
	);
	const field =
		isCompact && appearance === 'subtle' && !isDisabled ? (
			<Box xcss={styles.compactSubtleControl}>{select}</Box>
		) : (
			select
		);

	return hideLabel ? (
		field
	) : (
		<Field name="priority" label={labelText}>
			{() => field}
		</Field>
	);
};
